import React from "react";
import { Toaster } from "sonner";
import { Sparkles, Mountain, Sun, Compass, Camera, Tent, Utensils, HeartHandshake, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import XalucaOriginalsBadge from "@/components/XalucaOriginalsBadge";
import GeneralInquiryForm from "@/components/GeneralInquiryForm";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Trilingual copy block kept inside the component so the file remains
// drop-in self-contained — translators can edit a single place.
const COPY = {
  overline: { en: "Bespoke · Tailor-made", fr: "Sur-mesure · Personnalisé", es: "A medida · Personalizado" },
  title: {
    en: "Beyond the listed experiences — your southern Morocco, by design.",
    fr: "Au-delà des expériences listées — votre sud marocain, sur mesure.",
    es: "Más allá de las experiencias listadas — su sur de Marruecos, a medida.",
  },
  lead: {
    en: "We can organise and personalise almost any experience across southern Morocco — the Sahara dunes, the Atlas summits, hidden oases, fortified kasbahs, cultural encounters, private events, celebrations, gastronomy, adventure activities, photography expeditions and exclusive one-of-one moments.",
    fr: "Nous savons organiser et personnaliser presque toute expérience dans le sud marocain — les dunes du Sahara, les sommets de l'Atlas, les oasis cachées, les kasbahs fortifiées, les rencontres culturelles, les événements privés, les célébrations, la gastronomie, les activités d'aventure, les expéditions photographiques et les instants exclusifs.",
    es: "Sabemos organizar y personalizar casi cualquier experiencia en el sur de Marruecos — dunas del Sáhara, cumbres del Atlas, oasis escondidos, kasbahs fortificadas, encuentros culturales, eventos privados, celebraciones, gastronomía, actividades de aventura, expediciones fotográficas y momentos exclusivos.",
  },
  adapt: {
    en: "Every experience is adapted to your interests, group size, timing and preferences. No request is too unusual — we love a brief that asks something different.",
    fr: "Chaque expérience s'adapte à vos centres d'intérêt, à votre groupe, à votre rythme et à vos préférences. Aucune demande n'est trop inhabituelle — nous aimons les briefs qui sortent du cadre.",
    es: "Cada experiencia se adapta a sus intereses, tamaño de grupo, ritmo y preferencias. Ninguna petición es demasiado inusual — nos encantan los briefs que se salen de lo previsto.",
  },

  // Pill chips listing the realms of expertise. Icons match the Lucide
  // language used elsewhere on the site so the section feels native.
  realms: [
    { icon: Sun, label: { en: "Sahara desert", fr: "Désert du Sahara", es: "Desierto del Sáhara" } },
    { icon: Mountain, label: { en: "Atlas mountains", fr: "Atlas marocain", es: "Montañas del Atlas" } },
    { icon: Compass, label: { en: "Oases & kasbahs", fr: "Oasis & kasbahs", es: "Oasis y kasbahs" } },
    { icon: HeartHandshake, label: { en: "Cultural encounters", fr: "Rencontres culturelles", es: "Encuentros culturales" } },
    { icon: Tent, label: { en: "Private events & celebrations", fr: "Événements privés & célébrations", es: "Eventos privados y celebraciones" } },
    { icon: Utensils, label: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" } },
    { icon: Sparkles, label: { en: "Adventure & exclusives", fr: "Aventure & exclusivités", es: "Aventura y exclusivos" } },
    { icon: Camera, label: { en: "Photography trips", fr: "Voyages photo", es: "Viajes fotográficos" } },
  ],

  question_overline: { en: "Talk to us", fr: "Parlez-nous", es: "Háblenos" },
  question_title: {
    en: "Can't find the experience you're looking for?",
    fr: "Vous ne trouvez pas l'expérience que vous cherchez ?",
    es: "¿No encuentra la experiencia que busca?",
  },
  question_sub: {
    en: "Our concierges design fully personalised experiences based on your specific interests and travel goals. Tell us the trip you've been dreaming about — we'll build it around you.",
    fr: "Nos conciergeries composent des expériences entièrement personnalisées à partir de vos centres d'intérêt et de vos objectifs de voyage. Racontez-nous le voyage dont vous rêvez — nous le construirons autour de vous.",
    es: "Nuestros conserjes diseñan experiencias totalmente personalizadas a partir de sus intereses y objetivos de viaje. Cuéntenos el viaje que sueña — lo construimos a su alrededor.",
  },

  form_invitation_overline: { en: "Share your idea", fr: "Partagez votre idée", es: "Comparta su idea" },
  form_invitation_title: {
    en: "Let's create your experience together.",
    fr: "Composons votre expérience ensemble.",
    es: "Creemos su experiencia juntos.",
  },
  form_invitation_sub: {
    en: "Tell us about the group, your dates, the pace, the people you'd like to meet, the food you want to taste. The more detail, the more we can shape — and the faster we can come back with a proposal.",
    fr: "Parlez-nous du groupe, des dates, du rythme, des personnes que vous aimeriez rencontrer, des saveurs à découvrir. Plus c'est précis, mieux nous composons — et plus vite nous revenons vers vous avec une proposition.",
    es: "Háblenos del grupo, de las fechas, del ritmo, de las personas que desea conocer, de los sabores que quiere descubrir. Cuanto más detalle, mejor podremos componer — y antes le devolveremos una propuesta.",
  },
};

// Pre-fill values handed to the shared inquiry form so the concierge knows
// the lead came from the bespoke section (and not the generic /contact form).
const PREFILL_SUBJECT = {
  en: "Bespoke experience request — Xaluca Originals",
  fr: "Demande d'expérience sur-mesure — Xaluca Originals",
  es: "Solicitud de experiencia a medida — Xaluca Originals",
};
const PREFILL_MESSAGE = {
  en: "Hello Xaluca team,\n\nI'd love to design a custom experience in southern Morocco. Here's a quick brief:\n\n• Travelling: [number of guests, ages]\n• Dates: \n• What we're dreaming about: \n• Style / pace / interests: \n\nLooking forward to hearing what you can suggest.\n\nThank you,",
  fr: "Bonjour l'équipe Xaluca,\n\nJ'aimerais composer une expérience sur-mesure dans le sud du Maroc. Voici un bref aperçu :\n\n• Voyageurs : [nombre, âges]\n• Dates : \n• Ce dont nous rêvons : \n• Style / rythme / centres d'intérêt : \n\nDans l'attente de vos suggestions.\n\nMerci,",
  es: "Hola equipo Xaluca,\n\nMe encantaría diseñar una experiencia a medida en el sur de Marruecos. Aquí un breve resumen:\n\n• Viajeros: [número, edades]\n• Fechas: \n• Lo que soñamos: \n• Estilo / ritmo / intereses: \n\nQuedo a la espera de sus sugerencias.\n\nGracias,",
};

// Immersive cinematic backdrop — saturated southern Morocco landscape
// chosen to evoke both dunes and Atlas peaks at the same time.
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1473580044384-7ba9967e16a0?auto=format&fit=crop&w=2400&q=80";

const BespokeExperiencesSection = () => {
  const { lang } = useLanguage();
  return (
    <section
      className="relative isolate text-[#FDFBF7] overflow-hidden"
      data-testid="bespoke-experiences-section"
      // Stable CMS scope so the cinematic hero background photo gets a
      // deterministic slot id (`cms.bespoke-experiences.hero.bg`) in
      // /admin/images and the AutoImageEditor pencil in edit mode.
      data-cms-scope="bespoke-experiences.hero"
    >
      {/* Background: immersive southern-Morocco photography + tight gradient
          for legibility. Fixed-position parallax would feel premium on
          desktop but breaks scroll perf on mobile, so we keep it static. */}
      <img
        src={HERO_IMAGE}
        alt=""
        aria-hidden="true"
        data-cms-role="bg"
        className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(26,21,19,0.78) 0%, rgba(26,21,19,0.92) 38%, rgba(26,21,19,0.96) 100%)",
        }}
      />
      <span className="film-grain !z-[0] opacity-60" aria-hidden="true" />
      <BrandMonogramEdge size="xl" opacity={0.5} testIdPrefix="bespoke-section-brand" />

      <Toaster position="top-center" richColors />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40">
        {/* HEADLINE BLOCK */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <XalucaOriginalsBadge variant="dark" testid="bespoke-originals-badge" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#D4A373]">
                {pick(COPY.overline, lang)}
              </span>
            </div>
            <h2 className="font-serif-x mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02]">
              {pick(COPY.title, lang)}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-[#FDFBF7]/85">
              {pick(COPY.lead, lang)}
            </p>
            <p className="mt-6 text-sm md:text-base leading-relaxed text-[#FDFBF7]/65 border-l border-[#D4A373]/40 pl-5 italic">
              {pick(COPY.adapt, lang)}
            </p>
          </div>
        </div>

        {/* REALMS — pill row showing what we cover. Wraps on mobile. */}
        <ul
          className="flex flex-wrap gap-2.5 md:gap-3 mb-20 md:mb-28"
          data-testid="bespoke-realms"
        >
          {COPY.realms.map(({ icon: Icon, label }) => (
            <li
              key={label.en}
              className="inline-flex items-center gap-2 px-3.5 py-2 border border-[#FDFBF7]/15 bg-[#FDFBF7]/5 backdrop-blur-sm text-[11px] tracking-[0.18em] uppercase text-[#FDFBF7]/85 hover:border-[#D4A373]/60 hover:text-[#D4A373] transition-colors"
              data-testid={`bespoke-realm-${label.en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-3.5 h-3.5 text-[#D4A373]" strokeWidth={1.6} />
              {pick(label, lang)}
            </li>
          ))}
        </ul>

        {/* QUESTION + FORM — two-column on lg, stacked on mobile */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: question-driven copy */}
          <div className="lg:col-span-5 xl:col-span-5">
            <p className="overline !text-[#D4A373]">{pick(COPY.question_overline, lang)}</p>
            <h3 className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.08]">
              {pick(COPY.question_title, lang)}
            </h3>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#FDFBF7]/80">
              {pick(COPY.question_sub, lang)}
            </p>

            <div className="mt-10 pt-8 border-t border-[#FDFBF7]/15">
              <p className="overline !text-[#D4A373]">
                {pick(COPY.form_invitation_overline, lang)}
              </p>
              <p className="font-serif-x mt-3 text-2xl md:text-3xl text-[#FDFBF7] leading-snug flex items-start gap-3">
                <ArrowRight className="w-5 h-5 mt-2 text-[#C16542] shrink-0" strokeWidth={1.5} />
                <span>{pick(COPY.form_invitation_title, lang)}</span>
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-[#FDFBF7]/65">
                {pick(COPY.form_invitation_sub, lang)}
              </p>
            </div>
          </div>

          {/* Right: shared inquiry form, dark variant */}
          <div className="lg:col-span-7 xl:col-span-6 xl:col-start-7">
            <div
              className="bg-[#FDFBF7]/[0.04] backdrop-blur-md border border-[#FDFBF7]/12 p-6 md:p-10"
              data-testid="bespoke-form-wrap"
            >
              <GeneralInquiryForm
                variant="dark"
                testid="bespoke-inquiry"
                prefillSubject={pick(PREFILL_SUBJECT, lang)}
                prefillMessage={pick(PREFILL_MESSAGE, lang)}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BespokeExperiencesSection;
